/**
 * WeaveIn.ai Platform - Contact Activity Form
 * Log pre-enrollment outreach activities
 */

import React, { useState } from 'react';
import { Phone, Mail, MessageSquare, MapPin, FileText, Loader2, CheckCircle, X } from 'lucide-react';
import { ContactActivityCreate } from '@/services/api/contacts';

interface ContactActivityFormProps {
  contactId: number;
  programId?: number;
  onSubmit: (data: ContactActivityCreate) => Promise<boolean>;
  onClose?: () => void;
}

const ACTIVITY_TYPES = [
  { value: 'outreach_call', label: 'Outreach Call', icon: Phone },
  { value: 'outreach_sms', label: 'Outreach SMS', icon: MessageSquare },
  { value: 'outreach_email', label: 'Outreach Email', icon: Mail },
  { value: 'outreach_visit', label: 'Outreach Visit', icon: MapPin },
  { value: 'intake_meeting', label: 'Intake Meeting', icon: FileText },
  { value: 'eligibility_check', label: 'Eligibility Check', icon: FileText },
  { value: 'follow_up', label: 'Follow Up', icon: Phone },
  { value: 'document_request', label: 'Document Request', icon: FileText },
  { value: 'note', label: 'General Note', icon: FileText },
];

const CONTACT_OUTCOMES = [
  { value: 'reached', label: 'Reached' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'declined', label: 'Declined' },
  { value: 'scheduled', label: 'Scheduled Follow-up' },
];

export default function ContactActivityForm({ contactId, programId, onSubmit, onClose }: ContactActivityFormProps) {
  const [form, setForm] = useState<ContactActivityCreate>({
    activity_type: 'outreach_call',
    activity_name: '',
    contact_method: 'phone',
    contact_outcome: '',
    contact_direction: 'outbound',
    program_id: programId,
    notes: '',
    duration_minutes: undefined,
    is_billable: false,
    status: 'completed',
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const selectedType = ACTIVITY_TYPES.find((t) => t.value === form.activity_type);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        activity_name: form.activity_name || selectedType?.label || form.activity_type,
      };
      const ok = await onSubmit(data);
      if (ok) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          setForm((prev) => ({ ...prev, activity_name: '', notes: '', contact_outcome: '', duration_minutes: undefined }));
          onClose?.();
        }, 600);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Log Activity</h4>
        {onClose && (
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Activity type */}
      <div className="grid grid-cols-3 gap-1.5">
        {ACTIVITY_TYPES.map((t) => {
          const Icon = t.icon;
          const selected = form.activity_type === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, activity_type: t.value, activity_name: t.label }))}
              className={`flex items-center gap-1.5 px-2.5 py-2 text-xs rounded-lg border transition-all ${
                selected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                  : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Direction */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Direction</label>
          <select
            value={form.contact_direction}
            onChange={(e) => setForm((prev) => ({ ...prev, contact_direction: e.target.value }))}
            className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="outbound">Outbound</option>
            <option value="inbound">Inbound</option>
          </select>
        </div>

        {/* Outcome */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Outcome</label>
          <select
            value={form.contact_outcome || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, contact_outcome: e.target.value }))}
            className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">Select outcome...</option>
            {CONTACT_OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Duration */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Duration (min)</label>
          <input
            type="number"
            min={0}
            value={form.duration_minutes ?? ''}
            onChange={(e) => setForm((prev) => ({ ...prev, duration_minutes: e.target.value ? Number(e.target.value) : undefined }))}
            className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder="0"
          />
        </div>

        {/* Billable */}
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_billable}
              onChange={(e) => setForm((prev) => ({ ...prev, is_billable: e.target.checked }))}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Billable</span>
          </label>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes</label>
        <textarea
          value={form.notes || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          rows={2}
          className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
          placeholder="Activity notes..."
        />
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving || success}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : success ? <CheckCircle className="w-4 h-4" /> : null}
          {success ? 'Saved' : 'Log Activity'}
        </button>
      </div>
    </form>
  );
}
